import { DefaultLegacyGetOptions } from '@bymason/api-base';

export interface ApplicationData {
  application?: string;
  version?: string;
}

export interface PlatformFeature {
  features: string[];
  package: string;
}

export interface AppMap {
  [k: number]: {
    uid: number;
    packageNames: string[];
  } & MappedUsageCycles;
}

export interface UsageCycle {
  bytes?: number;
  bytesReceived?: number;
  bytesSent?: number;
  startTime?: string;
  endTime?: string;
}

// TODO: convert to static usage types ex: ('wifi' || 'mobile')
export interface MappedUsageCycle {
  [k: string]: UsageCycle;
}

export interface MappedUsageCycles {
  previousDay?: MappedUsageCycle;
  previousMonth?: MappedUsageCycle;
}

export interface DeviceNetworkUtilization {
  device?: MappedUsageCycles;
  apps?: AppMap;
}

export interface WifiInfo {
  adapterMacAddress?: string;
  bssid?: string;
  enabled?: boolean;
  ipaddress?: string;
  ssid?: string;
}

export interface SimInfo {
  carrier?: string;
  countryiso?: string;
  iccid?: string;
  mcc?: string;
  mnc?: string;
}

export interface BluetoothInfo {
  enabled?: boolean;
  macAddress?: string;
  name?: string;
  pairedDevices?: PairedDevice[];
}

export interface PairedDevice {
  macAddress: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  firmwareRevision?: string;
  hardwareRevision?: string;
  softwareRevision?: string;
  batteryLevel?: string;
  name: string;
  status?: string;
  peripheralUuid?: string;
  isPeripheral: boolean;
}

export interface DataUsageCycle {
  startTime?: string;
  endTime?: string;
  usages?: {
    type?: 'mobile' | 'wifi';
    bytesReceived?: number;
    bytesSent?: number;
  }[];
}

export interface DeviceDataUsage {
  usageCycles?: DataUsageCycle[];
  appsDataUsage?: {
    uid?: number;
    packageNames?: string[];
    usageCycles?: DataUsageCycle[];
  }[];
}

export interface DeviceNotification {
  id: string;
  type: string;
  date: string; // (createDate) UTC
  targets: string[];
  command: string;
  arguments: string[];
  expiration: string; // UTC
}

export interface DeviceStatus {
  deviceId: string;
  presence: {
    lastActive: {
      secondsAgo: number;
      date: string;
    };
    online: boolean;
  };
  pendingNotifications?: DeviceNotification[];
}

export interface DeviceLocation {
  latitude: string;
  longitude: string;
  accuracy: string;
  time: string;
}

export interface DeviceMasonOSInfo {
  version: string;
  incremental: string;
  release: string;
}

export interface DeviceConifgurations {
  device: string;
  product: string;
  project: string;
  version: string;
}

export interface DeviceOS {
  masonOS: DeviceMasonOSInfo;
  configuration: DeviceConifgurations;
  platformFeatures?: PlatformFeature[] | null;
}

export interface ConnectionInfo {
  bluetooth: BluetoothInfo;
  wifi: WifiInfo;
  sim?: SimInfo[];
  dataUsage?: DeviceDataUsage;
}

export interface UtilizationRow {
  id: string | number;
  package: string;
  wifi: number;
  mobile: number;
}

export interface UtiliaztionMap {
  apps: UtilizationRow[] | null;
  totals: UtilizationRow | null;
}

export interface DeviceModel {
  id: string;
  serialnumber: string;
  self: string;
  name: string;
  imei: string;
  lastcheckin: string;
  createdAt: string;
  os: DeviceOS;
  apps: ApplicationData[];
  networkUtilization?: DeviceNetworkUtilization;
  connection: ConnectionInfo;
  family: string;
  group: {
    name: string;
    self: string;
  };
  status: {
    osUpdated: boolean;
    appsUpdated: boolean;
    projectUpdated: boolean;
  };
  battery: {
    level: string | null;
  };
}

export interface GetDeviceOptions {
  deviceId: string;
}

export interface RenameDeviceOptions {
  deviceId: string;
  name: string;
}

export interface GetDevicesOptions extends DefaultLegacyGetOptions {
  // sort keys
  orderByCol?: 'createdAt' | 'lastHeartbeat';
  orderDir?: 'asc' | 'desc';

  // search keys
  searchType?: 'partial' | 'exact';
  searchQuery?: string;
  type?: 'all' | 'imei' | 'serial' | 'name';

  // filter keys (exact matches), can multi column filter on these
  deviceGroup?: string;
  deviceGroupID?: string;
  appsUpdated?: boolean;
  osUpdated?: boolean;
  projectUpdated?: boolean;
  provisioned?: boolean;
  maxBatteryPercent?: number; // 0 to 100 valid
  uuid?: string;
  name?: string;
}

export enum NotificationType {
  Ping = 'ping',
  Update = 'update',
  Locate = 'locate',
  Refresh = 'refresh',
}

export enum DangrousNotificationType {
  Reboot = 'reboot',
  Shutdown = 'shutdown',
  Wipe = 'wipe',
  Refurbish = 'refurbish',
}

export type NotifyType = NotificationType | DangrousNotificationType;

export interface NotifyDevicesOptions {
  type: NotifyType;
  devices: string[];
}

export type GetDevicePeripheralsOptions = GetDeviceOptions;

export interface DevicePeripheralResponse {
  data: {
    peripherals: Peripheral[] | null;
  };
}

export interface Peripheral {
  peripheralID: string;
  deviceID: string;
  accountID: string;
  lastModifiedBy: PeripheralLastModifiedBy;
  configurationData: PeripheralConfigurationData;
  createdAt: string;
  updatedAt: string;
  transactionID: string;
}

export interface PeripheralConfigurationData {
  deviceID: string;
  accountID: string;
  createdAt: string;
  peripheralID: string;
  // configurationData: ConfigurationDataConfigurationData;
}

// export interface ConfigurationDataConfigurationData {}

export interface PeripheralLastModifiedBy {
  id: string;
  type: string;
  accountID: string;
}
